import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import type { MultiValue } from 'react-select';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { GetTeamDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { teamName } from '../../../utils/teamName';

import { Option, Selector } from './Selector';

export interface TeamSelectorProps {
  projectId?: string;
  companyId?: string;
  companyIds?: string[];
  permission?: string | null;
  isMulti?: boolean;
  clearable?: boolean;
  readOnly?: boolean;
  value?: string | string[];
  className?: string;
  setOptions?: (employees: GetTeamDto[]) => void;
  onChange?: (option: Option | MultiValue<Option> | null | undefined) => void;
  tv?: boolean;
  allowableValues?: GetTeamDto[];
  preserveUnknownValue?: boolean;
  includeAssignedInactiveTeams?: boolean;
}

export default (props: TeamSelectorProps) => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const [teams, setTeams] = useState<GetTeamDto[] | undefined>(undefined);

  // dopisujemy nieaktywne zespoły, aby pozostały widoczne bo normalnie takie zespoły usuwamy z selectora
  const addMissingAssignedInactiveTeams = async (
    activeTeams: GetTeamDto[],
    value: string | string[] | undefined,
  ) => {
    const assignedIds = Array.isArray(value) ? value : value ? [value] : [];
    const missingIds = assignedIds.filter(
      id => !activeTeams.some(team => team.id === id),
    );
    if (!missingIds.length) {
      return activeTeams;
    }
    const missingTeams = await Promise.all(
      missingIds.map(id => pok.teams.getById(id).catch(() => null)),
    );
    return activeTeams.concat(
      missingTeams.filter((team): team is GetTeamDto => !!team),
    );
  };

  useEffect(() => {
    let mounted = true;
    let fetchTeams;

    if (props.allowableValues) {
      setTeams(props.allowableValues);
      return;
    }

    if (props.projectId) {
      fetchTeams = pok.teams.getAllByProjectId(props.projectId);
    } else if (props.permission) {
      fetchTeams = pok.teams.getMyForPermissionByCompanyIds(
        props.companyId ? [props.companyId] : (props.companyIds as string[]),
        props.permission,
      );
    } else if (props.companyId || props.companyIds?.length) {
      fetchTeams = pok.teams.getAllByCompanyIds(
        props.companyId ? [props.companyId] : (props.companyIds as string[]),
      );
    }

    if (fetchTeams) {
      fetchTeams
        .then(async data => {
          if (!mounted) {
            return;
          }
          const result = props.includeAssignedInactiveTeams
            ? await addMissingAssignedInactiveTeams(data, props.value)
            : data;
          if (mounted) {
            props.setOptions?.(props.tv ? result.filter(o => o.tv) : result);
            setTeams(props.tv ? result.filter(o => o.tv) : result);
          }
        })
        .catch(errorResponse => {
          notifications.caughtError(errorResponse);
        });
    } else {
      setTeams(undefined);
    }

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pok.teams,
    props.companyId,
    props.companyIds,
    notifications,
    props.projectId,
    props.allowableValues,
    props.includeAssignedInactiveTeams,
  ]);

  useEffect(() => {
    if (teams && props.onChange) {
      if (props.isMulti) {
        const newTeams = teams?.filter(({ id }) => props.value?.includes(id));
        if ((props.value?.length || 0) !== (newTeams?.length || 0)) {
          props.onChange(
            newTeams.map(v => ({ value: v.id, label: v.name }) as Option),
          );
        }
      } else if (
        !props.preserveUnknownValue &&
        props.value &&
        !teams.find(team => team.id === props.value)
      ) {
        props.onChange(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, props.value, props.isMulti]);

  const provider = async (text: string) => {
    const values =
      teams?.filter(v =>
        v.name?.toLocaleLowerCase().includes(text.toLocaleLowerCase()),
      ) || [];
    return values
      .map(v => ({
        label: teamName(v, v.company.shortname),
        value: v.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  if (!teams && (props.companyId || props.companyIds?.length)) {
    return <Skeleton />;
  }

  return (
    <Selector
      uniqueKey={'team-' + teams?.map(o => o.id).join('_')}
      className={props.className}
      isMulti={props.isMulti}
      readOnly={
        props.readOnly ||
        !teams ||
        (!props.allowableValues &&
          !props.companyId &&
          !props.companyIds?.length &&
          !props.projectId)
      }
      creatable={false}
      clearable={props.clearable}
      provider={provider}
      value={props.value}
      deletedValueName={id => pok.teams.getOptionById(id)}
      onChange={props.onChange}
    />
  );
};
